/**
 * Shared metric definitions and aggregation for Keyword Benchmark charts and tables.
 *
 * ── Master metric model ──────────────────────────────────────────────────────
 * Every benchmark metric is declared once in `BENCHMARK_METRICS`. Each entry has:
 *   - key / label / decimals / color  — display basics
 *   - group        — which on-screen section it renders in (see BENCHMARK_METRIC_GROUPS)
 *   - allowedModes — modes the backend returns it for (undefined = all modes)
 *   - shareable    — may appear in the pie / proportional views (only summable
 *                    count metrics; ratios, indices and percentages are not a
 *                    meaningful share of a whole)
 *   - format       — "percent" | "signedPercent" for %-valued / signed metrics
 *   - aggregation  — how values combine when MULTIPLE keywords are selected:
 *        "sum"        additive counts (publications, citations, grants, $)
 *        "avgWeighted" rates / indices / percentages → publication-weighted mean
 *        "max"        set-cardinalities & rank statistics that don't add across
 *                     topics (h-index family, faculty / researcher head-counts,
 *                     distinct collaborating countries)
 *
 * The derived lists (`BENCHMARK_CORE_STATS`, `BENCHMARK_BAR_STATS`,
 * `BENCHMARK_PIE_STATS`, `BENCHMARK_SUB_COLS`, `BENCHMARK_METRIC_SECTIONS`) all
 * project out of this single source so a new metric only needs one edit here.
 */

/** Mixed warm + cool institution colors for clear contrast in stacked charts (alternates hue families). */
export const BENCHMARK_INSTITUTION_COLORS = [
  "#1e3a5f",
  "#2f5f7c",
  "#5c79c1",
  "#7d74a6",
  "#8ba0b2",
  "#244b77",
  "#3d6f8d",
  "#6d88c8",
  "#8b80ad",
  "#9aacbb",
  "#2d547f",
  "#416f90",
  "#6f84ba",
  "#8478a0",
  "#9aabba",
  "#345f87",
  "#4e7596",
  "#7790c5",
  "#8c82aa",
  "#9aaebf",
  "#3b6184",
  "#567a99",
  "#7a93c3",
];

/**
 * Selectable chart palettes for the admin customization panel. `default` is the
 * built-in mixed blue palette; the others give the admin distinct schemes.
 * Colors cycle by entity index when there are more entities than swatches.
 */
export const BENCHMARK_PALETTES = {
  default: BENCHMARK_INSTITUTION_COLORS,
  colorblind: [
    "#0072B2",
    "#E69F00",
    "#009E73",
    "#CC79A7",
    "#56B4E9",
    "#D55E00",
    "#F0E442",
    "#000000",
  ],
  // Warm family, but spread across hue + lightness so neighbours are distinct.
  warm: [
    "#7f1d1d", // dark red
    "#f59e0b", // amber
    "#ea580c", // orange
    "#facc15", // yellow
    "#dc2626", // red
    "#92400e", // brown
    "#fb7185", // coral
    "#b45309", // dark amber
  ],
  // Cool family, spread across blues/teals/greens/purples + lightness.
  cool: [
    "#1e3a8a", // navy
    "#38bdf8", // sky
    "#059669", // emerald
    "#7c3aed", // violet
    "#0891b2", // cyan
    "#1d4ed8", // blue
    "#14b8a6", // teal
    "#4d7c0f", // olive
  ],
  // Grayscale ordered so consecutive entities alternate dark/light for contrast.
  grayscale: [
    "#111827", // near-black
    "#cbd5e1", // light
    "#4b5563", // dark-mid
    "#e5e7eb", // very light
    "#6b7280", // mid
    "#1f2937", // dark
    "#9ca3af", // light-mid
    "#374151", // darker-mid
  ],
};

/** Resolve a palette name to its color array (falls back to the default). */
export const getPaletteColors = (name) =>
  BENCHMARK_PALETTES[name] || BENCHMARK_PALETTES.default;

export const getBenchmarkInstitutionAbbreviation = (name) => {
  const skip = new Set(["of", "the", "at", "and", "–"]);
  return name
    .split(/[\s,]+/)
    .filter(Boolean)
    .filter((w) => !skip.has(w.toLowerCase()))
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 3);
};

// Metric groups, in render order. `core` is drawn as the main bar + pie scaling
// block; the rest render as their own labeled bar-only sections.
export const BENCHMARK_METRIC_GROUPS = {
  CORE: "core",
  IMPACT: "impact",
  CAPACITY: "capacity",
  MOMENTUM: "momentum",
};

/**
 * Single source of truth for every benchmark metric.
 */
export const BENCHMARK_METRICS = [
  // ── Core volume (bar + pie) ───────────────────────────────────────────────
  {
    key: "publication_count",
    label: "Publication Count",
    group: BENCHMARK_METRIC_GROUPS.CORE,
    color: "#3b82f6",
    decimals: 0,
    aggregation: "sum",
    shareable: true,
  },
  {
    key: "citation_count",
    label: "Citation Count",
    group: BENCHMARK_METRIC_GROUPS.CORE,
    color: "#10b981",
    decimals: 0,
    aggregation: "sum",
    shareable: true,
  },
  {
    key: "grants_count",
    label: "Grants",
    group: BENCHMARK_METRIC_GROUPS.CORE,
    color: "#22c55e",
    decimals: 0,
    aggregation: "sum",
    shareable: true,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "total_amount_awarded",
    label: "Amount Awarded",
    group: BENCHMARK_METRIC_GROUPS.CORE,
    color: "#0ea5e9",
    decimals: 0,
    aggregation: "sum",
    shareable: true,
    allowedModes: ["researcher"],
  },
  {
    key: "faculty_count",
    label: "Faculty Count",
    group: BENCHMARK_METRIC_GROUPS.CORE,
    color: "#f59e0b",
    decimals: 0,
    // Head-count of faculty active in the topic — the same person can appear
    // under several keywords, so take the max rather than double-counting.
    aggregation: "max",
    shareable: true,
    allowedModes: ["institution", "unit", "venue"],
  },

  // ── Citation impact ───────────────────────────────────────────────────────
  {
    key: "h_index",
    label: "H-Index",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#6366f1",
    decimals: 0,
    aggregation: "max",
    shareable: false,
  },
  {
    key: "h5_index",
    label: "H5-Index",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#818cf8",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "h10_index",
    label: "H10-Index",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#a5b4fc",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "i10_index",
    label: "i10-Index",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#4f46e5",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "g_index",
    label: "G-Index",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#7c3aed",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "median_citations_per_paper",
    label: "Median Citations / Paper",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#ec4899",
    decimals: 1,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "percent_cited_papers",
    label: "% Cited Papers",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#14b8a6",
    decimals: 1,
    format: "percent",
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution", "researcher", "unit"],
  },
  {
    key: "percent_papers_top_10",
    label: "% in Top 10% Cited",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#f43f5e",
    decimals: 1,
    format: "percent",
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution"],
  },
  {
    key: "percent_papers_top_1",
    label: "% in Top 1% Cited",
    group: BENCHMARK_METRIC_GROUPS.IMPACT,
    color: "#e11d48",
    decimals: 1,
    format: "percent",
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution"],
  },

  // ── Capacity & productivity ───────────────────────────────────────────────
  {
    key: "publication_count_per_faculty",
    label: "Publications / Faculty",
    group: BENCHMARK_METRIC_GROUPS.CAPACITY,
    color: "#a855f7",
    decimals: 2,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution", "unit", "venue"],
  },
  {
    key: "active_researcher_count",
    label: "Active Researchers",
    group: BENCHMARK_METRIC_GROUPS.CAPACITY,
    color: "#f59e0b",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution", "unit"],
  },
  {
    key: "publications_per_active_researcher",
    label: "Publications / Active Researcher",
    group: BENCHMARK_METRIC_GROUPS.CAPACITY,
    color: "#d97706",
    decimals: 2,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution", "unit"],
  },
  {
    key: "median_publications_per_researcher",
    label: "Median Publications / Researcher",
    group: BENCHMARK_METRIC_GROUPS.CAPACITY,
    color: "#ca8a04",
    decimals: 1,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution", "unit"],
  },
  {
    key: "avg_publication_by_year",
    label: "Average Publications / Year",
    group: BENCHMARK_METRIC_GROUPS.CAPACITY,
    color: "#14b8a6",
    decimals: 2,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["researcher"],
  },

  // ── Momentum & reach ──────────────────────────────────────────────────────
  {
    key: "recent_5yr_publication_share",
    label: "Recent 5-yr Publication Share",
    group: BENCHMARK_METRIC_GROUPS.MOMENTUM,
    color: "#0ea5e9",
    decimals: 1,
    format: "percent",
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution"],
  },
  {
    key: "publication_cagr",
    label: "Publication CAGR",
    group: BENCHMARK_METRIC_GROUPS.MOMENTUM,
    color: "#ef4444",
    decimals: 1,
    format: "signedPercent",
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution"],
  },
  {
    key: "distinct_collaborating_countries",
    label: "Collaborating Countries",
    group: BENCHMARK_METRIC_GROUPS.MOMENTUM,
    color: "#06b6d4",
    decimals: 0,
    aggregation: "max",
    shareable: false,
    allowedModes: ["institution"],
  },
  {
    key: "specialization_index",
    label: "Specialization Index",
    group: BENCHMARK_METRIC_GROUPS.MOMENTUM,
    color: "#8b5cf6",
    decimals: 2,
    aggregation: "avgWeighted",
    shareable: false,
    allowedModes: ["institution"],
    // Relative-specialization (RCA) baseline: 1.0 = proportional presence,
    // above = over-represented in the topic. Drawn as a reference line.
    referenceValue: 1,
  },
];

/** Fast key → metric lookup. */
export const BENCHMARK_METRIC_BY_KEY = BENCHMARK_METRICS.reduce((acc, m) => {
  acc[m.key] = m;
  return acc;
}, {});

/** Core volume metrics — the main bar + pie scaling block. */
export const BENCHMARK_CORE_STATS = BENCHMARK_METRICS.filter(
  (m) => m.group === BENCHMARK_METRIC_GROUPS.CORE
);

/**
 * Full metric pool for bar-style views (advanced heatmap dropdown, exports).
 * Kept named `BENCHMARK_BAR_STATS` for backwards compatibility with existing
 * imports.
 */
export const BENCHMARK_BAR_STATS = BENCHMARK_METRICS;

/** Pie / proportional views only make sense for summable count metrics. */
export const BENCHMARK_PIE_STATS = BENCHMARK_METRICS.filter((m) => m.shareable).map(
  ({ key, label, decimals, allowedModes, format }) => ({
    key,
    label,
    decimals,
    allowedModes,
    format,
  })
);

/** Breakdown table columns — every metric. */
export const BENCHMARK_SUB_COLS = BENCHMARK_METRICS.map(
  ({ label, key, decimals, allowedModes, format }) => ({
    label,
    key,
    decimals,
    allowedModes,
    format,
  })
);

/**
 * Ordered, labeled metric sections rendered below the core scaling block.
 * (Core is rendered separately as bar + pie.)
 */
export const BENCHMARK_METRIC_SECTIONS = [
  {
    id: BENCHMARK_METRIC_GROUPS.IMPACT,
    title: "Citation Impact",
    subtitle:
      "Depth and quality of citations — index families and highly-cited share.",
    stats: BENCHMARK_METRICS.filter(
      (m) => m.group === BENCHMARK_METRIC_GROUPS.IMPACT
    ),
  },
  {
    id: BENCHMARK_METRIC_GROUPS.CAPACITY,
    title: "Capacity & Productivity",
    subtitle: "Output relative to the people producing it.",
    stats: BENCHMARK_METRICS.filter(
      (m) => m.group === BENCHMARK_METRIC_GROUPS.CAPACITY
    ),
  },
  {
    id: BENCHMARK_METRIC_GROUPS.MOMENTUM,
    title: "Momentum & Reach",
    subtitle: "Recent trajectory, international reach and topic specialization.",
    stats: BENCHMARK_METRICS.filter(
      (m) => m.group === BENCHMARK_METRIC_GROUPS.MOMENTUM
    ),
  },
];

export const formatBenchmarkMetricShortLabel = (label) => {
  const normalized = String(label || "").trim().toLowerCase();
  if (normalized === "publication count" || normalized === "publications") {
    return "Pubs.";
  }
  if (normalized === "publications / faculty") {
    return "Pubs / Faculty";
  }
  if (normalized === "faculty count") {
    return "Faculty";
  }
  if (normalized === "average publications / year") {
    return "Avg Pubs / Year";
  }
  if (normalized === "median citations / paper") {
    return "Median Cites / Paper";
  }
  if (normalized === "publications / active researcher") {
    return "Pubs / Researcher";
  }
  if (normalized === "median publications / researcher") {
    return "Median Pubs / Researcher";
  }
  if (normalized === "recent 5-yr publication share") {
    return "Recent 5-yr Share";
  }
  return label;
};

/**
 * Filter metrics to only those available in the given benchmark mode.
 * Metrics with no `allowedModes` are shown in all modes.
 */
export const filterMetricsByMode = (metrics, activeMode) => {
  if (!activeMode) return metrics;
  return metrics.filter((m) => !m.allowedModes || m.allowedModes.includes(activeMode));
};

/** Magnitudes below this render in full (no K/M/B shorthand). */
export const BENCHMARK_FULL_NUMBER_BELOW = 50000;

/**
 * @param {number} num
 * @param {number} decimals
 * @param {{ abbreviate?: boolean }} [options] — `abbreviate: false` always uses full locale numbers (e.g. PDF export).
 */
export const formatBenchmarkChartValue = (num, decimals = 0, options = {}) => {
  const { abbreviate = true } = options;
  if (num == null || Number.isNaN(num)) return decimals > 0 ? Number(0).toFixed(decimals) : "0";
  const abs = Math.abs(num);

  const formatFull = () => {
    if (decimals > 0) {
      const rounded = Math.round(num * 10 ** decimals) / 10 ** decimals;
      return rounded.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      });
    }
    return Math.round(num).toLocaleString();
  };

  if (!abbreviate) {
    return formatFull();
  }

  if (abs < BENCHMARK_FULL_NUMBER_BELOW) {
    return formatFull();
  }

  if (abs >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (abs >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (abs >= 1e3) return Math.round(num / 1e3) + "K";
  if (decimals > 0) {
    const rounded = Math.round(num * 10 ** decimals) / 10 ** decimals;
    return rounded.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  }
  return Math.round(num).toLocaleString();
};

/**
 * Format a value for a given metric definition, honoring its `format`
 * (percent / signedPercent) and `decimals`. Percentages and signed metrics are
 * never abbreviated. Nullish / non-numeric values render as an em dash.
 */
export const formatBenchmarkStatValue = (value, stat, options = {}) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const num = Number(value);
  const decimals = stat?.decimals ?? 0;
  if (stat?.format === "percent") {
    return `${formatBenchmarkChartValue(num, decimals, { abbreviate: false })}%`;
  }
  if (stat?.format === "signedPercent") {
    const sign = num > 0 ? "+" : "";
    return `${sign}${formatBenchmarkChartValue(num, decimals, { abbreviate: false })}%`;
  }
  return formatBenchmarkChartValue(num, decimals, options);
};

/**
 * Combine per-(keyword, entity) records into one record per entity across all
 * selected keywords, honoring each metric's `aggregation`:
 *   - sum         additive counts
 *   - avgWeighted publication-weighted mean (rates, indices, percentages)
 *   - max         non-additive head-counts / rank statistics
 * With a single keyword selected every strategy returns that keyword's value.
 */
export const aggregateBenchmarkByInstitution = (rawData, institutionIds) => {
  if (!rawData.length) return [];

  const acc = {};

  rawData.forEach((keywordEntry) => {
    const keyword = Object.keys(keywordEntry)[0];
    (keywordEntry[keyword] || []).forEach((inst) => {
      const id = inst.institution_id;
      if (id == null) return;
      if (!acc[id]) {
        acc[id] = {
          institution_id: id,
          name: inst.name,
          photo_url: inst.photo_url,
          _sum: {},
          _wsum: {},
          _wtot: {},
          _max: {},
        };
      }
      const a = acc[id];
      // Weight for averaged metrics = publications behind this (keyword, entity).
      const weight = Number(inst.publication_count) || 0;

      BENCHMARK_METRICS.forEach((m) => {
        const v = Number(inst[m.key]);
        if (!Number.isFinite(v)) return;
        if (m.aggregation === "avgWeighted") {
          a._wsum[m.key] = (a._wsum[m.key] || 0) + v * weight;
          a._wtot[m.key] = (a._wtot[m.key] || 0) + weight;
        } else if (m.aggregation === "max") {
          a._max[m.key] = a._max[m.key] == null ? v : Math.max(a._max[m.key], v);
        } else {
          a._sum[m.key] = (a._sum[m.key] || 0) + v;
        }
      });
    });
  });

  return institutionIds
    .map((id) => acc[id])
    .filter(Boolean)
    .map((a) => {
      const out = {
        institution_id: a.institution_id,
        name: a.name,
        photo_url: a.photo_url,
      };
      BENCHMARK_METRICS.forEach((m) => {
        if (m.aggregation === "avgWeighted") {
          const wtot = a._wtot[m.key] || 0;
          out[m.key] = wtot > 0 ? a._wsum[m.key] / wtot : 0;
        } else if (m.aggregation === "max") {
          out[m.key] = a._max[m.key] != null ? a._max[m.key] : 0;
        } else {
          out[m.key] = a._sum[m.key] != null ? a._sum[m.key] : 0;
        }
      });
      return out;
    });
};
